import React, { useCallback, useEffect, useState } from 'react';

/** Safari (< 16.4) and iPadOS still ship the prefixed Fullscreen API. */
interface WebkitDocument extends Document {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}

interface WebkitElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

/** iPhone has no element fullscreen — only the video element itself can go fullscreen. */
interface WebkitVideoElement extends HTMLVideoElement {
  webkitEnterFullscreen?: () => void;
}

export interface Fullscreen {
  isSupported: boolean;
  isFullscreen: boolean;
  toggle: () => void;
}

function getFullscreenElement(): Element | null {
  const doc = document as WebkitDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

/**
 * Fullscreen is a document-wide state, so a player may only claim it for the element it owns:
 * its own wrapper, or the media element it captured for the iPhone fallback.
 */
function isOwnFullscreenElement(
  wrapper: HTMLElement | null,
  player: HTMLVideoElement | null,
): boolean {
  const fullscreenElement = getFullscreenElement();

  if (!fullscreenElement) {
    return false;
  }

  return fullscreenElement === wrapper || fullscreenElement === player;
}

function getIphoneFallbackTarget(player: HTMLVideoElement | null): WebkitVideoElement | null {
  const target = player as WebkitVideoElement | null;
  if (target && typeof target.webkitEnterFullscreen === 'function') {
    return target;
  }

  return null;
}

function detectSupport(player: HTMLVideoElement | null): boolean {
  const doc = document as WebkitDocument;
  const documentSupport = doc.fullscreenEnabled ?? doc.webkitFullscreenEnabled ?? false;
  return Boolean(documentSupport) || getIphoneFallbackTarget(player) !== null;
}

function requestFullscreen(wrapper: WebkitElement, player: HTMLVideoElement | null): void {
  if (typeof wrapper.requestFullscreen === 'function') {
    Promise.resolve(wrapper.requestFullscreen()).catch(() => {});
    return;
  }

  if (typeof wrapper.webkitRequestFullscreen === 'function') {
    Promise.resolve(wrapper.webkitRequestFullscreen()).catch(() => {});
    return;
  }

  const fallbackTarget = getIphoneFallbackTarget(player);
  // Calling this before the first frame is decoded throws on iOS.
  if (fallbackTarget && fallbackTarget.readyState >= 1) {
    fallbackTarget.webkitEnterFullscreen?.();
  }
}

function exitFullscreen(): void {
  const doc = document as WebkitDocument;
  if (typeof doc.exitFullscreen === 'function') {
    Promise.resolve(doc.exitFullscreen()).catch(() => {});
    return;
  }

  if (typeof doc.webkitExitFullscreen === 'function') {
    Promise.resolve(doc.webkitExitFullscreen()).catch(() => {});
  }
}

/**
 * @param isMediaReady Re-runs the support detection whenever the media element is captured or
 * released: on iPhone that element is the only thing able to report fullscreen support at all.
 */
export function useFullscreen(
  wrapperRef: React.RefObject<HTMLElement>,
  playerRef: React.RefObject<HTMLVideoElement>,
  isMediaReady: boolean,
): Fullscreen {
  const [isSupported, setIsSupported] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    setIsSupported(detectSupport(playerRef.current));
  }, [playerRef, isMediaReady]);

  useEffect(() => {
    const syncFullscreenState = () =>
      setIsFullscreen(isOwnFullscreenElement(wrapperRef.current, playerRef.current));
    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('webkitfullscreenchange', syncFullscreenState);

    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      document.removeEventListener('webkitfullscreenchange', syncFullscreenState);
    };
  }, [wrapperRef, playerRef]);

  const toggle = useCallback(() => {
    const wrapper = wrapperRef.current as WebkitElement | null;

    // Another player holding the fullscreen state must not turn this button into an exit button.
    if (isOwnFullscreenElement(wrapper, playerRef.current)) {
      exitFullscreen();
      return;
    }

    if (!wrapper) {
      return;
    }

    requestFullscreen(wrapper, playerRef.current);
  }, [wrapperRef, playerRef]);

  return { isSupported, isFullscreen, toggle };
}
